/**
 * Is this image url on an origin the BROWSER can read bytes from?
 *
 * A download is not a render. `<img src>` needs no CORS, but `downloadFile` /
 * `downloadImageAsFormat` both begin with `await fetch(url)` — and a third-party origin that serves
 * no `Access-Control-Allow-Origin` makes that fetch reject with a bare `TypeError: Failed to fetch`.
 * The image is visible on the page the whole time, which is exactly why this reads as "the download
 * button does nothing" (REF-6494).
 *
 * Measured on production against a Browzwear/Stylezone image (`polytropon.stylezone.com`):
 *   • `<img src>`                     → loads, 434×1214
 *   • `fetch(url)`                    → TypeError: Failed to fetch
 *   • `fetch(url, {mode:"no-cors"})`  → opaque response — the resource exists, only CORS is missing
 *
 * So the test cannot be "does the image load"; it has to be the origin. Anything not on this
 * allowlist must be fetched by the BACKEND instead (`utils/process-download`, which re-hosts the
 * bytes on app_cdn) rather than by the browser.
 *
 * Kept deliberately narrow — an unknown host counts as external, because guessing wrong in that
 * direction only costs one server round-trip, while guessing wrong the other way is a dead button.
 */
const READABLE_HOSTS = [
  // Our own buckets: app_cdn (prod/stage/preprod) and refabric-clean (dev).
  "storage.googleapis.com",
  // The imgproxy resize layer in front of app_cdn (previewUrl).
  "img.refabric.com",
];

export const isBrowserReadableImageUrl = (url: string): boolean => {
  // Relative paths, data: and blob: are same-origin / in-memory — always readable.
  if (!url || url.startsWith("data:") || url.startsWith("blob:") || !/^https?:\/\//i.test(url)) return true;

  try {
    const { host } = new URL(url);

    return host === window.location.host || READABLE_HOSTS.includes(host);
  } catch {
    // Unparseable — treat as same-origin rather than forcing every odd url through the BE.
    return true;
  }
};

/** The inverse, for readability at call sites that branch on "needs the backend to fetch it". */
export const isExternalImageUrl = (url: string): boolean => !isBrowserReadableImageUrl(url);
